import {createContext, useContext, useCallback, useEffect, useMemo, useState} from 'react'
import {
    LayoutModeType,
    LayoutOffcanvasStatesType,
    LayoutOrientationType,
    LayoutState,
    LayoutType,
    MenuType,
    OffcanvasControlType,
    LayoutThemeType,
    TopBarThemeType, LayoutWidthType, IconMenuThemeType,
} from '@/types/layout'

import useLocalStorage from '@/hooks/useLocalStorage'
import { useUserProfile } from '@/hooks/useUserProfile'

import {ChildrenType} from '@/types/component-props'
import {toggleDocumentAttribute} from '@/utils/layout'

const ThemeContext = createContext<LayoutType | undefined>(undefined)

const useLayoutContext = () => {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useLayoutContext can only be used within LayoutProvider')
    }
    return context
}

const LayoutProvider = ({children}: ChildrenType) => {

    const INIT_STATE: LayoutState = {
        theme: 'light',
        orientation: 'vertical',
        mode: 'fluid',
        width: "fluid",
        topBar: {theme: 'light'},
        menu: {
            theme: 'light',
            size: 'default',
            position: 'fixed'
        },
        twoColumn: {
            iconMenuTheme: "light"
        },
        showTwoToneIcons: false,
        showUserInfo: false
    }

    const { profile, updateProfile, updateProfileImmediate, isSaving } = useUserProfile()
    const [savedSettings, setSavedSettings] = useLocalStorage<LayoutState>('__STONKFU_CONFIG__', INIT_STATE)
    const [previewSettings, setPreviewSettings] = useState<LayoutState>(savedSettings)
    const [justSaved, setJustSaved] = useState<boolean>(false)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)
    
    // Sync preview with saved settings when saved settings change
    useEffect(() => {
        console.log('🔄 [LayoutContext] Syncing preview settings with saved settings:', {
            from: previewSettings,
            to: savedSettings
        });
        setPreviewSettings(savedSettings)
    }, [savedSettings])
    
    // Load user profile preferences on mount
    useEffect(() => {
        console.log('🔄 [LayoutContext] Profile preferences loading effect triggered', {
            hasProfile: !!profile,
            hasLayoutPrefs: !!profile?.layout_preferences,
            profileLayoutPrefs: profile?.layout_preferences
        });
        
        if (profile?.layout_preferences) {
            try {
                const userPrefs = typeof profile.layout_preferences === 'string' 
                    ? JSON.parse(profile.layout_preferences) 
                    : profile.layout_preferences
                
                console.log('🔄 [LayoutContext] Parsed user preferences:', userPrefs);
                console.log('🔄 [LayoutContext] Current savedSettings before merge:', savedSettings);
                
                // Map user preferences to layout state format and update saved settings
                const mergedSettings = {
                    theme: userPrefs.theme || userPrefs.mode || savedSettings.theme, // Try both theme and mode fields
                    orientation: userPrefs.layoutOrientation || savedSettings.orientation,
                    mode: savedSettings.mode, // Keep mode from local storage
                    width: userPrefs.layoutWidth || savedSettings.width,
                    topBar: { theme: userPrefs.topbarColor || savedSettings.topBar.theme },
                    menu: {
                        theme: userPrefs.sidebarColor || savedSettings.menu.theme,
                        size: userPrefs.sidebarSize || savedSettings.menu.size,
                        position: savedSettings.menu.position
                    },
                    twoColumn: {
                        iconMenuTheme: savedSettings.twoColumn.iconMenuTheme
                    },
                    showTwoToneIcons: userPrefs.twoToneIcons ?? savedSettings.showTwoToneIcons,
                    showUserInfo: savedSettings.showUserInfo
                }
                
                console.log('🔄 [LayoutContext] Merged settings to apply:', mergedSettings);
                console.log('🔄 [LayoutContext] Key theme values:', {
                    mainTheme: mergedSettings.theme,
                    sidebarColor: mergedSettings.menu.theme,
                    topbarColor: mergedSettings.topBar.theme
                });
                
                setSavedSettings(mergedSettings)
            } catch (e) {
                console.warn('Failed to parse layout preferences from profile:', e)
            }
        } else if (profile && !profile.layout_preferences) {
            console.log('🔄 [LayoutContext] Profile loaded but no layout preferences found, using defaults');
        }
    }, [profile?.layout_preferences]) // Only run when profile preferences change
    
    // Function to commit preview settings to saved settings and sync to database
    const commitSettings = useCallback((newSettings: LayoutState) => {
        console.log('💾 [LayoutContext] commitSettings called with:', newSettings)
        setSavedSettings(newSettings)
        setPreviewSettings(newSettings)
        
        // Save to user profile if user is logged in
        if (profile) {
            const layoutPrefs = {
                theme: newSettings.theme,
                mode: newSettings.theme, // Keep for compatibility
                sidebarColor: newSettings.menu.theme,
                topbarColor: newSettings.topBar.theme,
                sidebarSize: newSettings.menu.size,
                layoutWidth: newSettings.width,
                layoutOrientation: newSettings.orientation,
                twoToneIcons: newSettings.showTwoToneIcons
            }
            
            console.log('💾 [LayoutContext] Saving layout preferences to profile:', layoutPrefs)
            updateProfileImmediate({
                layout_preferences: layoutPrefs
            })
        } else {
            console.warn('⚠️ [LayoutContext] No user profile found, only saving locally')
        }
    }, [profile, updateProfileImmediate, setSavedSettings])

    // Function to save current preview settings (called by Save Settings button)
    const saveSettings = useCallback(() => {
        commitSettings(previewSettings)
        setJustSaved(true)
        setHasUnsavedChanges(false)
        // Clear the justSaved flag after a short delay
        setTimeout(() => setJustSaved(false), 100)
    }, [previewSettings, commitSettings])

    // Function to revert preview settings to last saved state
    const revertSettings = useCallback(() => {
        console.log('🔄 [LayoutContext] Reverting to saved settings')
        setPreviewSettings(savedSettings)
        setHasUnsavedChanges(false)
    }, [savedSettings])

    const [offcanvasStates, setOffcanvasStates] = useState<LayoutOffcanvasStatesType>({
        showThemeCustomizer: false,
        showHorizontalMenu: false,
        showBackdrop: false,
    })

    // Update preview settings only (no save to database)
    const updatePreviewSettings = useCallback((_newSettings: Partial<LayoutState>) => {
        setPreviewSettings(prev => ({...prev, ..._newSettings}))
        setHasUnsavedChanges(true)
    }, [])

    const changeTheme = useCallback((newTheme: LayoutThemeType) => {
        updatePreviewSettings({theme: newTheme})
    }, [updatePreviewSettings])

    const changeLayoutMode = useCallback((newMode: LayoutModeType) => {
        updatePreviewSettings({mode: newMode})
    }, [updatePreviewSettings])

    const changeLayoutOrientation = useCallback((newOrientation: LayoutOrientationType) => {
        updatePreviewSettings({orientation: newOrientation})
    }, [updatePreviewSettings])

    const changeLayoutWidth = useCallback((newWidth: LayoutWidthType) => {
        updatePreviewSettings({width: newWidth})
    }, [updatePreviewSettings])

    const changeTopBarTheme = useCallback((newTheme: TopBarThemeType) => {
        updatePreviewSettings({topBar: {theme: newTheme}})
    }, [updatePreviewSettings])

    const changeMenuTheme = useCallback((newTheme: MenuType['theme']) => {
        setPreviewSettings(prev => ({...prev, menu: {...prev.menu, theme: newTheme}}))
    }, [])

    const changeMenuSize = useCallback((newSize: MenuType['size']) => {
        setPreviewSettings(prev => ({...prev, menu: {...prev.menu, size: newSize}}))
    }, [])

    const changeMenuPosition = useCallback((newPosition: MenuType['position']) => {
        setPreviewSettings(prev => ({...prev, menu: {...prev.menu, position: newPosition}}))
    }, [])

    const changeIconMenuTheme = useCallback((newTheme: IconMenuThemeType) => {
        updatePreviewSettings({twoColumn: {iconMenuTheme: newTheme}})
    }, [updatePreviewSettings])

    const toggleTwoToneIcons = useCallback(() => {
        setPreviewSettings(prev => ({...prev, showTwoToneIcons: !prev.showTwoToneIcons}))
    }, [])

    const toggleUserInfo = useCallback(() => {
        setPreviewSettings(prev => ({...prev, showUserInfo: !prev.showUserInfo}))
    }, [])

    const toggleThemeCustomizer: OffcanvasControlType['toggle'] = useCallback(() => {
        const isClosing = offcanvasStates.showThemeCustomizer
        
        console.log('🎨 [LayoutContext] Theme customizer toggle:', {
            isClosing,
            justSaved,
            willRevert: isClosing && !justSaved
        });
        
        // REMOVED: Automatic revert on close
        // This was causing database-loaded settings to be overwritten
        // Users should explicitly click "Reset" if they want to revert changes
        
        setOffcanvasStates({...offcanvasStates, showThemeCustomizer: !offcanvasStates.showThemeCustomizer})
    }, [offcanvasStates, justSaved])

    const toggleHorizontalMenu: OffcanvasControlType['toggle'] = useCallback(() => {
        setOffcanvasStates({...offcanvasStates, showHorizontalMenu: !offcanvasStates.showHorizontalMenu})
    }, [offcanvasStates])

    const themeCustomizer: LayoutType['themeCustomizer'] = {
        open: offcanvasStates.showThemeCustomizer,
        toggle: toggleThemeCustomizer,
    }

    const horizontalMenu: LayoutType['horizontalMenu'] = {
        open: offcanvasStates.showHorizontalMenu,
        toggle: toggleHorizontalMenu,
    }

    // toggle backdrop
    const toggleBackdrop = useCallback(() => {
        const htmlTag = document.getElementsByTagName('html')[0]
        if (offcanvasStates.showBackdrop) htmlTag.classList.remove('sidebar-enable')
        else htmlTag.classList.add('sidebar-enable')
        setOffcanvasStates({...offcanvasStates, showBackdrop: !offcanvasStates.showBackdrop})
    }, [offcanvasStates.showBackdrop])

    useEffect(() => {
        console.log('🎨 [LayoutContext] Applying DOM attributes:', {
            theme: previewSettings.theme,
            menuColor: previewSettings.menu.theme,
            topbarColor: previewSettings.topBar.theme,
            fullPreviewSettings: previewSettings
        });
        
        toggleDocumentAttribute('data-bs-theme', previewSettings.theme)
        toggleDocumentAttribute('data-topbar-color', previewSettings.topBar.theme)
        toggleDocumentAttribute('data-menu-color', previewSettings.menu.theme)
        toggleDocumentAttribute('data-sidenav-size', previewSettings.menu.size)
        toggleDocumentAttribute('data-layout-mode', previewSettings.mode)
        toggleDocumentAttribute('data-layout', previewSettings.orientation)
        toggleDocumentAttribute('data-two-column-color', previewSettings.twoColumn.iconMenuTheme)

        return () => {
            toggleDocumentAttribute('data-bs-theme', previewSettings.theme, true)
            toggleDocumentAttribute('data-topbar-color', previewSettings.topBar.theme, true)
            toggleDocumentAttribute('data-menu-color', previewSettings.menu.theme, true)
            toggleDocumentAttribute('data-sidenav-size', previewSettings.menu.size, true)
            toggleDocumentAttribute('data-layout-mode', previewSettings.mode, true)
            toggleDocumentAttribute('data-layout', previewSettings.orientation, true)
            toggleDocumentAttribute('data-two-column-color', previewSettings.twoColumn.iconMenuTheme, true)
        }
    }, [previewSettings])

    const resetSettings = useCallback(() => {
        setPreviewSettings(INIT_STATE)
        setHasUnsavedChanges(false)
    }, [])

    return (
        <ThemeContext.Provider
            value={useMemo(
                () => ({
                    ...previewSettings,
                    horizontalMenu,
                    changeTheme,
                    changeLayoutMode,
                    changeLayoutOrientation,
                    changeLayoutWidth,
                    changeTopBarTheme,
                    changeMenuTheme,
                    changeMenuSize,
                    changeMenuPosition,
                    changeIconMenuTheme,
                    toggleTwoToneIcons,
                    toggleUserInfo,
                    themeCustomizer,
                    toggleBackdrop,
                    resetSettings,
                    saveSettings,
                    revertSettings,
                    isSaving,
                    hasUnsavedChanges,
                }),
                [previewSettings, offcanvasStates, isSaving, hasUnsavedChanges],
            )}>
            {children}
            {offcanvasStates.showBackdrop && <div className="offcanvas-backdrop fade show" onClick={toggleBackdrop}/>}
        </ThemeContext.Provider>
    )
}

export {LayoutProvider, useLayoutContext}
